<template>
    <!-- The one place a project (or a standard library) is created and edited:
         its identity and every setting the old Project settings view held. -->
    <v-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)"
        :max-width="isPhone ? undefined : 760" :fullscreen="isPhone" scrollable>
        <v-card :loading="loading ? 'accent' : false">
            <v-card-title>
                {{ standard_library ? t("standard_library") : t("project") }}:
                {{ form.name }}
            </v-card-title>
            <v-divider />
            <v-card-text>
                <v-skeleton-loader v-if="loading" type="text@6" />
                <v-form v-else ref="form" @keyup.enter.native="save">
                    <h3 class="mb-3 font-weight-light text-grey">
                        {{ standard_library ? t("standard_library_params") : t("project_params") }}
                    </h3>
                    <v-row dense>
                        <v-col v-if="!standard_library" cols="12" md="6">
                            <SuperProjectField v-model="form.super_project" />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field variant="outlined" density="compact" :label="t('name')" :rules="rules"
                                v-model="form.name" />
                        </v-col>
                        <v-col v-if="!standard_library" cols="12" md="6">
                            <v-text-field variant="outlined" density="compact" :label="t('client')"
                                :rules="rules" v-model="form.client_name" />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field variant="outlined" density="compact"
                                :label="standard_library ? t('std_number') : t('pr_number')" :rules="rules"
                                v-model="form.project_number" />
                        </v-col>
                    </v-row>

                    <h3 class="mb-3 mt-2 font-weight-light text-grey">
                        {{ t("project_settings") }}
                    </h3>
                    <v-row dense>
                        <v-col v-if="!standard_library" cols="12" md="6">
                            <SelectContact :label="t('engineering_contact')" v-model="form.engineering_contact" />
                        </v-col>
                        <v-col cols="12" md="6">
                            <SelectIFCPS v-model="form.ifc_parsing_setup" />
                            <!-- Changing the setup is a move
                                 (docs/ifcps-save-analysis-proposal.md §6.4):
                                 what it costs, before Save. -->
                            <div v-if="move_hint" class="text-caption mt-n2 mb-2"
                                :class="move?.will_analyse ? 'text-orange-darken-4' : 'text-medium-emphasis'">
                                <v-icon size="x-small" class="mr-1">{{ move_hint_icon }}</v-icon>
                                {{ move_hint }}
                            </div>
                        </v-col>
                        <v-col v-if="has_multipanel" cols="12" md="6">
                            <SelectMultipanelConfig v-model="form.multipanel_config" />
                        </v-col>
                        <v-col v-if="!standard_library" cols="12" md="6">
                            <SelectSimpleProject only_stdlibs multiple v-model="form.linked_stdlibs" />
                        </v-col>
                        <v-col cols="12" md="6">
                            <SelectPDFScale v-model="form.meta.pdf_render_scale" />
                        </v-col>
                        <template v-if="!standard_library">
                            <v-col cols="12" md="6">
                                <SelectDate :label="t('start_date')" v-model="form.start_date" />
                            </v-col>
                            <v-col cols="12" md="6">
                                <SelectDate :label="t('delivery_date')" v-model="form.delivery_date" />
                            </v-col>
                        </template>
                    </v-row>
                </v-form>
            </v-card-text>
            <v-divider />
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="accent" variant="flat" @click="save" :loading="saving" :disabled="saving || loading">
                    <v-icon start> mdi-check </v-icon>
                    {{ t("save") }}
                </v-btn>
                <v-btn color="secondary" variant="text" @click="$emit('update:modelValue', false)">
                    {{ t("close") }}
                </v-btn>
            </v-card-actions>
        </v-card>

        <!-- Confirmed from one design: an analysis can take up to a minute. -->
        <v-dialog v-model="confirm_open" :max-width="isPhone ? undefined : 520" :fullscreen="isPhone" persistent>
            <v-card v-if="move">
                <v-card-title class="d-flex align-center ga-2">
                    <v-icon>mdi-swap-horizontal</v-icon>
                    {{ t("ifcps_change_setup_title") }}
                </v-card-title>
                <v-card-text>
                    <p class="mb-3">{{ move_text }}</p>
                    <div class="d-flex flex-wrap ga-2 mb-3">
                        <v-sheet border rounded class="px-3 py-2 move-fact">
                            <div class="pit-mono text-h6">{{ move.designs }}</div>
                            <div class="text-caption text-medium-emphasis">{{ t("ifcps_designs_to_analyse") }}</div>
                        </v-sheet>
                        <v-sheet border rounded class="px-3 py-2 move-fact">
                            <div class="pit-mono text-h6">{{ move.in_production }}</div>
                            <div class="text-caption text-medium-emphasis">{{ t("in_production") }}</div>
                        </v-sheet>
                        <v-sheet border rounded class="px-3 py-2 move-fact">
                            <div class="pit-mono text-h6">{{ move.ready_units }}</div>
                            <div class="text-caption text-medium-emphasis">{{ t("ready_units") }}</div>
                        </v-sheet>
                        <v-sheet border rounded class="px-3 py-2 move-fact">
                            <div class="pit-mono text-h6">~{{ move_minutes }}</div>
                            <div class="text-caption text-medium-emphasis">{{ t("minutes") }}</div>
                        </v-sheet>
                    </div>
                    <v-alert type="info" variant="tonal" density="compact" color="purple-darken-2" icon="mdi-refresh">
                        {{ t("ifcps_move_analyses") }}
                    </v-alert>
                </v-card-text>
                <v-card-actions class="px-4 pb-3 flex-wrap ga-2">
                    <v-spacer />
                    <v-btn color="accent" variant="flat" prepend-icon="mdi-check" :loading="saving"
                        @click="confirm_open = false; do_save()">
                        {{ t("ifcps_save_and_analyse").replace("{designs}", move.designs) }}
                    </v-btn>
                    <v-btn color="secondary" variant="text" @click="confirm_open = false">{{ t("cancel") }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
import { backend_api_v2 } from "@/utils/apiv2"
import { usePitStoreBasics } from "@/stores/app_common"
import { useCompanyStore } from "@/stores/company"
import { useSelectedStore } from "@/stores/selected"
import { useSessionStore } from "@/stores/session"
import { useIsPhone } from "@/composables/display"
import SuperProjectField from "@/components/company/projects/SuperProjectField"
import SelectIFCPS from "@/components/ifcps/SelectIFCPS.vue"
import SelectMultipanelConfig from "@/components/multipanel/SelectMultipanelConfig.vue"
import SelectSimpleProject from "@/components/fields/SelectSimpleProject.vue"
import SelectContact from "@/components/company/contacts/SelectContact.vue"
import SelectPDFScale from "@/components/fields/SelectPDFScale.vue"
import SelectDate from "@/components/fields/SelectDate.vue"
import { estimateMinutes } from "@/utils/ifcps_versions"

// The fields this dialog edits. A save sends these and nothing else, so it
// cannot overwrite what other screens own on the same row (phases, archived,
// production state).
const PROJECT_FIELDS = [
    "name", "super_project", "client_name", "project_number", "engineering_contact",
    "ifc_parsing_setup", "multipanel_config", "linked_stdlibs", "start_date", "delivery_date",
]
const STDLIB_FIELDS = ["name", "project_number", "ifc_parsing_setup", "multipanel_config"]

export default {
    setup() {
        return {
            ...usePitStoreBasics(),
            company_store: useCompanyStore(),
            selected_store: useSelectedStore(),
            session_store: useSessionStore(),
            ...useIsPhone(),
        }
    },
    components: {
        SuperProjectField, SelectIFCPS, SelectMultipanelConfig, SelectSimpleProject,
        SelectContact, SelectPDFScale, SelectDate,
    },
    props: {
        modelValue: { type: Boolean, default: false },
        // null creates a new one.
        project_uuid: { type: String, default: null },
        standard_library: { type: Boolean, default: false },
    },
    emits: ["update:modelValue"],
    data: () => ({
        loading: false,
        saving: false,
        form: { meta: {} },
        // The full project as the API serves it, for its `meta`: the save
        // writes `meta` whole, so it starts from the stored one.
        stored_meta: {},
        rules: [(v) => !!v || "Required"],
        // The setup the project had when the form opened, and what moving it
        // to the one selected now costs (`move_projects`, dry run).
        original_ps: null,
        move: null,
        confirm_open: false,
    }),
    computed: {
        has_multipanel() { return this.session_store.hasAnyModule(["m_multipanel"]) },
        fields() { return this.standard_library ? STDLIB_FIELDS : PROJECT_FIELDS },
        url() { return this.standard_library ? "company/standard_libraries/" : "company/projects/" },
        setup_changed() {
            return !!this.form.uuid && !!this.form.ifc_parsing_setup
                && this.form.ifc_parsing_setup != this.original_ps
        },
        move_minutes() { return estimateMinutes(this.move?.designs || 0) },
        move_hint_icon() {
            if (this.move?.will_analyse) return "mdi-alert-outline"
            return this.move?.manual_analysis ? "mdi-information-outline" : "mdi-file-cog-outline"
        },
        move_hint() {
            if (!this.setup_changed || !this.move) return ""
            // The company analyses by hand: the save analyses nothing.
            if (this.move.manual_analysis) {
                return this.t("ifcps_move_manual_hint").replace("{designs}", this.move.designs)
            }
            if (!this.move.will_analyse) return this.t("ifcps_files_only")
            return this.t("ifcps_saving_analyses").replace("{designs}", this.move.designs)
        },
        move_text() {
            const name = (uuid) => {
                const ps = this.company_store.ifc_ps.find((p) => p.uuid == uuid)
                return ps ? `${ps.name} v${ps.version || 1}` : "—"
            }
            return this.t("ifcps_change_setup_text")
                .replace("{project}", this.form.name || "")
                .replace("{from}", name(this.original_ps))
                .replace("{to}", name(this.form.ifc_parsing_setup))
        },
    },
    watch: {
        modelValue(open) {
            if (open) this.open()
        },
        "form.ifc_parsing_setup"() {
            this.check_move()
        },
    },
    methods: {
        async check_move() {
            this.move = null
            if (!this.setup_changed) return
            const target = this.form.ifc_parsing_setup
            try {
                const data = await this.company_store.moveProjectsPreview(target, [this.form.uuid])
                // The select may have moved on while this was asked.
                if (target == this.form.ifc_parsing_setup) this.move = data.projects?.[0] || null
            } catch (e) {
                this.move = null
            }
        },
        async open() {
            this.saving = false
            this.original_ps = null
            this.move = null
            if (!this.project_uuid) {
                this.stored_meta = {}
                this.form = this.standard_library
                    ? { name: "", project_number: "", super_project: "std", standard_library: true,
                        linked_stdlibs: [], ifc_parsing_setup: null, multipanel_config: null, meta: {} }
                    : { name: "", super_project: "", client_name: "", project_number: "",
                        engineering_contact: null, ifc_parsing_setup: null, multipanel_config: null,
                        linked_stdlibs: [], start_date: null, delivery_date: null,
                        standard_library: false, meta: {} }
                return
            }
            // The list the tables read leaves out meta, linked_stdlibs and
            // multipanel_config, so the form is filled from the full project.
            this.loading = true
            try {
                const { data } = await backend_api_v2.get(this.url + this.project_uuid + "/")
                this.stored_meta = data.meta || {}
                const form = { uuid: data.uuid, meta: { pdf_render_scale: this.stored_meta.pdf_render_scale } }
                for (const key of this.fields) form[key] = data[key] ?? null
                if (!this.standard_library) form.linked_stdlibs = data.linked_stdlibs || []
                this.original_ps = form.ifc_parsing_setup
                this.form = form
            } catch (e) {
                this.$bus.emit("notification", { timeout: 5000, text: `Can't load project: ${e}`, color: "error" })
                this.$emit("update:modelValue", false)
            } finally {
                this.loading = false
            }
        },
        async save() {
            this.saving = true
            await this.$refs.form.validate()
            if (!this.$refs.form.isValid) {
                this.saving = false
                this.$bus.emit("notification", { timeout: 5000, text: this.t("invalid_form"), color: "warning" })
                return
            }
            // A move that analyses designs is confirmed first, from one design.
            if (this.setup_changed && this.move?.will_analyse && this.move.designs > 0) {
                this.saving = false
                this.confirm_open = true
                return
            }
            return this.do_save()
        },
        async do_save() {
            this.saving = true
            const meta = { ...this.stored_meta, pdf_render_scale: this.form.meta.pdf_render_scale }
            let res
            if (this.form.uuid) {
                const payload = { uuid: this.form.uuid, meta }
                for (const key of this.fields) payload[key] = this.form[key]
                res = this.standard_library
                    ? await this.company_store.putStandardLibrary(payload)
                    : await this.company_store.putProject(payload)
                // The open project is a copy of its own: keep it in step.
                if (res?.data && res.data.uuid == this.selected_store.selected_project?.uuid) {
                    this.selected_store.setProject(res.data)
                }
            } else {
                const payload = { ...this.form, meta }
                res = this.standard_library
                    ? await this.company_store.postStandardLibrary(payload)
                    : await this.company_store.postProject(payload)
            }
            this.saving = false
            // The store has already told the user when the save failed.
            if (res?.data) {
                // The projects per version moved.
                if (this.setup_changed) this.company_store.loadIFCPSs()
                this.$emit("update:modelValue", false)
            }
        },
    },
}
</script>

<style scoped>
.move-fact {
    min-width: 104px;
}
</style>
